/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

/**
 * Cross-domain cache invalidation helpers.
 *
 * The read models that render dashboards, analytics, quality scores and run insights are fed by
 * data that changes in *other* domains (a submission is created/run, a config/version/measure is
 * edited). Historically each mutation only invalidated its own namespace, so those derived views
 * stayed stale until a hard refresh. These helpers give mutations one place to invalidate every
 * cache their change can affect. See docs/specs/portal-data-freshness.md.
 *
 * Query keys are written here as literal roots that mirror the per-hook factories (`submissionKeys`,
 * `dashboardKeys`, `analyticsKeys`, `qualityBreakdownKeys`, `insightsKeys`, `submissionsListKeys`).
 * They are intentionally literals rather than imports so this shared helper stays decoupled from the
 * hook modules — invalidation matches by key prefix, and those modules are frequently mocked in tests.
 */

import type { QueryClient } from "@tanstack/react-query";

/**
 * Invalidate every cache a submission change or completed DQ run can affect: the two submission
 * list namespaces, the dashboard + analytics read models, and — when a submission id is given —
 * that submission's detail subtree (covers files/measures/summary/denominator/timeline via prefix,
 * plus the detail page's own `["submission", id]` object), its quality-score breakdown, and its
 * run insights.
 */
export function invalidateSubmissionDerived(queryClient: QueryClient, submissionId?: string): void {
  void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
  void queryClient.invalidateQueries({ queryKey: ["analytics"] });
  void queryClient.invalidateQueries({ queryKey: ["submissions", "list"] });
  void queryClient.invalidateQueries({ queryKey: ["submissions-list"] });
  if (submissionId) {
    // A single submission is cached under TWO live roots today: `submissionKeys.detail(id)` =
    // ["submissions", "detail", id] (used by useSubmission and the detail subtree) and the detail
    // page's own ["submission", id] query. Both are invalidated here on purpose — for any given
    // submission only one root is actually populated, so the other invalidation is a cheap no-op
    // scan, never a bug. Unifying these onto one root is Phase 2 of docs/specs/portal-data-freshness.md
    // (it also touches useBreadcrumbs, hence deferred); until then keep BOTH lines in lockstep with
    // invalidateSubmissionScoped so a run in progress and a terminal refresh clear the same roots.
    void queryClient.invalidateQueries({ queryKey: ["submissions", "detail", submissionId] });
    void queryClient.invalidateQueries({ queryKey: ["submission", submissionId] });
    void queryClient.invalidateQueries({
      queryKey: ["quality-score-breakdown", "submission", submissionId],
    });
    void queryClient.invalidateQueries({ queryKey: ["run-insights", "submission", submissionId] });
  }
}

/**
 * Narrow, per-tick refresh for the submission-results screen while a run is in progress: just the
 * submission object (`["submission", id]` — status/KPIs) and its detail subtree
 * (`["submissions", "detail", id]` — measure summary, files, etc.). Deliberately excludes the
 * dashboard/analytics/list/insights/quality caches so an 8s poll doesn't churn the whole cache;
 * the full cross-domain refresh is done once when the run reaches a terminal state.
 */
export function invalidateSubmissionScoped(queryClient: QueryClient, submissionId: string): void {
  void queryClient.invalidateQueries({ queryKey: ["submission", submissionId] });
  void queryClient.invalidateQueries({ queryKey: ["submissions", "detail", submissionId] });
}

/**
 * Invalidate caches affected by an admin config / version / measure / threshold change: the
 * dashboard + analytics read models re-serve config-derived values (version tile, quality
 * thresholds, active/total measure counts) under their own keys, so they must be refreshed too.
 */
export function invalidateConfigDerived(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
  void queryClient.invalidateQueries({ queryKey: ["analytics"] });
}
